import { Link } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";

/**
 * AppHome — Übersichts-Dashboard nach Login.
 * Zeigt 5 Tool-Kacheln + ggf. „Letzte Aktivität" wenn implementiert.
 */
export default function AppHome() {
  const { user } = useAuth();
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">
          Willkommen zurück{user ? `, ${user.email.split("@")[0]}` : ""}
        </h1>
        <p className="mt-2 text-slate-400">
          Such dir aus, was du heute machen willst — alle Werkzeuge sind direkt verfügbar.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Tile
          to="/app/quick"
          icon="⚡"
          title="Schnellfrage"
          body="In 60 Sekunden eine Mock-Jury-Einschätzung aus einer kurzen Beschreibung."
          accent="court"
          badge="schnell"
        />
        <Tile
          to="/app/case"
          icon="⚖"
          title="Fall analysieren"
          body="PDF + Beschreibung hochladen, 50-LLM-Schwarm urteilt + Anwalt-Followup."
          accent="emerald"
          badge="ausführlich"
        />
        <Tile
          to="/app/letter"
          icon="✉️"
          title="Brief entwerfen"
          body="Widerspruch, Mahnung, Mietminderung, EG261 oder Gewährleistung — als DOCX."
          accent="amber"
          badge="DOCX"
        />
        <Tile
          to="/app/structuring"
          icon="🏛"
          title="Steuer-Strukturierung"
          body="Weltweite Strukturen vergleichen + Berater-Vermittlung. Vergleich, keine Empfehlung."
          accent="violet"
          badge="weltweit"
        />
        <Tile
          to="/app/history"
          icon="📚"
          title="Meine Historie"
          body="Alle bisherigen Trials, Briefe und Strukturierungen — durchsuchbar."
          accent="slate"
        />
        <Tile
          to="/app/affiliate"
          icon="💸"
          title="Affiliate-Portal"
          body="3-Tier-Provision: 25 % L1, 10 % L2, 5 % L3 auf jeden referierten Verkauf."
          accent="rose"
          badge="bis 25 %"
        />
      </div>

      <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-6">
        <h2 className="text-lg font-semibold mb-2">Schnell-Tipps</h2>
        <ul className="text-sm text-slate-400 space-y-2">
          <li>• <strong>Schnellfrage</strong> ist der schnellste Einstieg — gib einfach 2-3 Sätze ein und du hast in unter 1 Min eine Einschätzung.</li>
          <li>• <strong>Brief entwerfen</strong> funktioniert auch ohne vorherige Mock-Jury — ideal wenn du schon weißt, was du willst.</li>
          <li>• <strong>Steuer-Strukturierung</strong> sammelt deine Lebenslage und vergleicht weltweit alle Optionen — strenger Vergleich, keine Beratung.</li>
        </ul>
      </div>
    </div>
  );
}

const ACCENTS: Record<string, string> = {
  court: "from-court-900/40 via-slate-900/40 to-slate-900/40 border-court-700/40 hover:border-court-500",
  emerald: "from-emerald-900/30 via-slate-900/40 to-slate-900/40 border-emerald-700/40 hover:border-emerald-500",
  amber: "from-amber-900/30 via-slate-900/40 to-slate-900/40 border-amber-700/40 hover:border-amber-500",
  violet: "from-violet-900/30 via-slate-900/40 to-slate-900/40 border-violet-700/40 hover:border-violet-500",
  slate: "from-slate-900/40 via-slate-900/40 to-slate-900/40 border-slate-700/40 hover:border-slate-500",
  rose: "from-rose-900/30 via-slate-900/40 to-slate-900/40 border-rose-700/40 hover:border-rose-500",
};

function Tile({
  to,
  icon,
  title,
  body,
  accent,
  badge,
}: {
  to: string;
  icon: string;
  title: string;
  body: string;
  accent: keyof typeof ACCENTS;
  badge?: string;
}) {
  return (
    <Link
      to={to}
      className={`group relative rounded-2xl border bg-gradient-to-br p-6 transition ${ACCENTS[accent]}`}
    >
      {badge && (
        <span className="absolute top-4 right-4 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-slate-950/60 border border-slate-700/60 text-slate-400">
          {badge}
        </span>
      )}
      <div className="text-3xl mb-3">{icon}</div>
      <h3 className="font-semibold text-lg group-hover:text-white transition">{title}</h3>
      <p className="text-sm text-slate-400 mt-2 leading-relaxed">{body}</p>
      <span className="mt-4 inline-flex items-center gap-1 text-xs text-slate-500 group-hover:text-slate-300 transition">
        Öffnen <span className="group-hover:translate-x-1 transition">→</span>
      </span>
    </Link>
  );
}
